import { Link, useNavigate } from "react-router-dom";
import { MoreVertical, FileText } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildEnrollmentProfileActions } from "@/components/actions";
import { AdeProfile } from "@/hooks/useAdeProfiles";
import { Badge } from "@/components/ui/badge";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { SortableHeader } from "@/components/ui/table-cells";
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useCompactMode } from "@/hooks/useCompactMode";

interface EnrollmentProfilesListProps {
  profiles: AdeProfile[];
  view: "grid" | "list";
  searchQuery?: string;
  onDelete?: (id: string) => void;
}

export const EnrollmentProfilesList = ({ 
  profiles, 
  view, 
  searchQuery = "",
  onDelete 
}: EnrollmentProfilesListProps) => {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const { formatDate } = useUserTimezone();
  const { cx } = useCompactMode();
  const navigate = useNavigate();

  const columns: ColumnDef<AdeProfile>[] = [
    {
      accessorKey: "icon",
      header: "",
      cell: () => (
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
          <FileText className="h-4 w-4 text-primary" />
        </div>
      ),
      size: 50,
      enableSorting: false,
    },
    {
      accessorKey: "profile_name",
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
        <Link 
            to={`/admin/enrollment-profiles/${row.original.id}`}
            className="font-medium hover:text-primary underline-offset-4 hover:underline"
          >
            {row.getValue("profile_name")}
          </Link>
          {row.original.is_draft && (
            <Badge variant="secondary" className="text-xs">Draft</Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => <SortableHeader column={column}>Description</SortableHeader>,
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {row.getValue("description") || "No description"}
        </div>
      ),
    },
    {
      accessorKey: "abm_connection",
      header: "ABM Account",
      cell: ({ row }) => {
        const connection = row.original.abm_connection;
        return (
          <Badge variant="secondary">
            {connection?.name || "Unknown"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "department",
      header: "Department",
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {row.getValue("department") || "N/A"}
        </div>
      ),
    },
    {
      accessorKey: "device_count",
      header: ({ column }) => <SortableHeader column={column}>Devices</SortableHeader>,
      cell: ({ row }) => (
        <div className="text-sm">
          {row.getValue("device_count") || 0}
        </div>
      ),
    },
    {
      id: "settings",
      header: "Settings",
      cell: ({ row }) => {
        const profile = row.original;
        return (
          <div className="flex gap-1 flex-wrap">
            {profile.is_mandatory && (
              <Badge variant="default" className="text-xs">Mandatory</Badge>
            )}
            {profile.is_mdm_removable && (
              <Badge variant="outline" className="text-xs">MDM Removable</Badge>
            )}
            {profile.auto_advance_setup && (
              <Badge variant="secondary" className="text-xs">Auto-advance</Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => {
        const date = row.getValue("created_at") as string;
        return date ? formatDate(date) : "N/A";
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const profile = row.original;
        return (
          <ActionsDropdown
            actions={buildEnrollmentProfileActions({
              surface: "list",
              onView: () => navigate(`/admin/enrollment-profiles/${profile.id}`),
              onEdit: () => navigate(`/admin/enrollment-profiles/${profile.id}/edit`),
              onDelete: onDelete ? () => onDelete(profile.id) : undefined,
            })}
          />
        );
      },
    },
  ];

  // Filter profiles based on search query
  const filteredProfiles = profiles.filter((profile) =>
    profile.profile_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    profile.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    profile.abm_connection?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    profile.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (filteredProfiles.length === 0) {
    return (
      <div className="text-center py-12">
        {profiles.length === 0 ? (
          <>
            <p className="text-muted-foreground text-lg">No enrollment profiles found</p>
            <div className="mt-4">
            <Button asChild>
              <Link to="/admin/enrollment-profiles/new">
                <Plus className="mr-2 h-4 w-4" />
                Create your first enrollment profile
              </Link>
            </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted-foreground text-lg">No profiles match your search.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your search criteria.
            </p>
          </>
        )}
      </div>
    );
  }

  const content = view === "grid" ? (
    <div className={cx("grid gap-4 grid-cols-1 md:grid-cols-2", "grid gap-3 grid-cols-1 md:grid-cols-2")}>
      {filteredProfiles.map((profile) => (
        <GlassCard 
          key={profile.id} 
          variant="default"
          hover={false}
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate(`/admin/enrollment-profiles/${profile.id}`)}
        >
          <GlassCardHeader className={cx("pb-2 p-4", "pb-1.5 p-3")}>
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <GlassCardTitle className="text-lg font-semibold hover:text-primary transition-colors">
                      {profile.profile_name}
                    </GlassCardTitle>
                    {profile.is_draft && (
                      <Badge variant="secondary" className="text-xs">Draft</Badge>
                    )}
                  </div>
                </div>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <ActionsDropdown
                  actions={buildEnrollmentProfileActions({
                    surface: "list",
                    onView: () => navigate(`/admin/enrollment-profiles/${profile.id}`),
                    onEdit: () => navigate(`/admin/enrollment-profiles/${profile.id}/edit`),
                    onDelete: onDelete ? () => onDelete(profile.id) : undefined,
                  })}
                />
              </div>
            </div>
            <GlassCardDescription className="mt-1">
              {profile.description || "No description"}
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className={cx("space-y-3 p-4 pt-0", "space-y-2 p-3 pt-0")}>
            <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <span className="font-medium">ABM:</span>
                <Badge variant="secondary">{profile.abm_connection?.name || "Unknown"}</Badge>
              </div>
              {profile.department && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">Dept:</span>
                  <span>{profile.department}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <span className="font-medium">Devices:</span>
                <span>{profile.device_count || 0}</span>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1">
              {profile.is_mandatory && (
                <Badge variant="default" className="text-xs">Mandatory</Badge>
              )}
              {profile.is_mdm_removable && (
                <Badge variant="outline" className="text-xs">MDM Removable</Badge>
              )}
              {profile.auto_advance_setup && (
                <Badge variant="secondary" className="text-xs">Auto-advance</Badge>
              )}
            </div>

            {profile.created_at && (
              <div className="text-xs text-muted-foreground pt-2 border-t">
                Created {formatDate(profile.created_at)}
              </div>
            )}
          </GlassCardContent>
        </GlassCard>
      ))}
    </div>
  ) : (
    <DataTable
      data={filteredProfiles}
      columns={columns}
      enableRowSelection={true}
      enableColumnResizing={true}
      enableSorting={true}
      columnSizing={columnSizing}
      onColumnSizingChange={setColumnSizing}
      emptyMessage={
        profiles.length === 0 
          ? "No enrollment profiles found. Create your first enrollment profile to get started."
          : "No profiles match your search. Try adjusting your search criteria."
      }
    />
  );

  return content;
};
